import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useMatches } from "react-router-dom";

import ViewDocumentModal from "../../pages/Documents/components/ViewDocumentModal";
import { useAskPerformYardStore } from "./AskPerformYardStore";
import { ChatContainer } from "./components/ChatContainer";
import ChatErrorBoundary from "./components/ChatErrorBoundary";
import ChatTrigger from "./components/ChatTrigger";
import { CompetencyCoachPopup } from "./components/CompetencyCoachPopup";
import { FormBuildingCoachPopup } from "./components/FormBuildingCoachPopup";
import { GoalCoachPopup } from "./components/GoalCoachPopup";
import { AskPyPanel } from "./components/panel/AskPyPanel";

import {
	useAiSettings,
	useHasAccessibleDocuments,
	useHasGoalCategories,
	useRbacPrimaryRolePermissions,
} from "../../api/hooks";
import { useAskPerformYardVisible } from "./hooks/useAskPerformYardVisible";
import { useAskPyTriggerVisible } from "./hooks/useAskPyTriggerVisible";
import { useCompetencyAgentVisible } from "./hooks/useCompetencyAgentVisible";
import { useFormBuildingCoachEnabled } from "./hooks/useFormBuildingCoachEnabled";
import { useGoalCoachEnabled } from "./hooks/useGoalCoachEnabled";
import { useAuthoringReviewReportId, useCurrentFormBuilderFormId } from "./hooks/useIsAuthoringReviewForm";
import { useReportBuilderVisible } from "./hooks/useReportBuilderVisible";
import { useReportSave } from "./hooks/useReportSave";

import { getUserID } from "../../selectors/app";

import RouterTags from "../../constants/RouterTags";

export { useAskPerformYardVisible } from "./hooks/useAskPerformYardVisible";

/** Conversational AI chat interface for answering questions from company documents. */
const AskPerformYard: React.FC = () => {
	const showLegacySurfaces = useAskPerformYardVisible();
	const triggerVisible = useAskPyTriggerVisible();
	const documentModal = useAskPerformYardStore((state) => state.documentModal);
	const closeDocumentModal = useAskPerformYardStore((state) => state.closeDocumentModal);
	const resetChat = useAskPerformYardStore((state) => state.resetChat);
	const setHasNoDocuments = useAskPerformYardStore((state) => state.setHasNoDocuments);
	const setHasGoalCategories = useAskPerformYardStore((state) => state.setHasGoalCategories);
	const updateGreetingMessage = useAskPerformYardStore((state) => state.updateGreetingMessage);
	const setIsOnFormBuilderPage = useAskPerformYardStore((state) => state.setIsOnFormBuilderPage);
	const setCurrentFormBuilderFormId = useAskPerformYardStore((state) => state.setCurrentFormBuilderFormId);
	const setAuthoringReviewReportId = useAskPerformYardStore((state) => state.setAuthoringReviewReportId);
	const setIsOnCompetencyPage = useAskPerformYardStore((state) => state.setIsOnCompetencyPage);

	// Load AI settings for dynamic greeting
	const { data: aiSettings } = useAiSettings();
	const competencyAgentVisible = useCompetencyAgentVisible();
	const reportBuilderVisible = useReportBuilderVisible();
	const goalCoachEnabled = useGoalCoachEnabled();
	const formBuildingCoachEnabled = useFormBuildingCoachEnabled();
	const isOnGoalCreationPage = useAskPerformYardStore((state) => state.isOnGoalCreationPage);

	// Gate form creation on RBAC, not just company AI setting — checkFormPermission tool gates on the same permission.
	const { data: reviewPermissions } = useRbacPrimaryRolePermissions("review");
	const canCreateForms = reviewPermissions?.includes("reviews:createAdminCompanyForms") ?? false;

	// Mount the report-save hook so its auto-save effect fires when the interviewer submits.
	// Safe to mount unconditionally — the effect only runs when reportBuilder.status === "pending",
	// which never gets set for users without the AI report builder harness.
	useReportSave();
	const matches = useMatches();
	const isOnFormBuilderPage = matches.some((m) =>
		((m.handle as { tags?: string[] } | undefined)?.tags ?? []).includes(RouterTags.FORM_BUILDER)
	);
	useEffect(() => {
		setIsOnFormBuilderPage(isOnFormBuilderPage);
	}, [isOnFormBuilderPage, setIsOnFormBuilderPage]);

	// Mirrored into the store so useAskStream can attach it to every request, and restored
	// form-question cards can compare against it, without either re-deriving it from the route.
	const currentFormBuilderFormId = useCurrentFormBuilderFormId();
	useEffect(() => {
		setCurrentFormBuilderFormId(currentFormBuilderFormId);
	}, [currentFormBuilderFormId, setCurrentFormBuilderFormId]);

	// Mirrored into the store so useAskStream can attach it to every request without re-deriving it
	// from the DOM; the coach agent re-checks authorship against this id server-side.
	const authoringReviewReportId = useAuthoringReviewReportId();
	useEffect(() => {
		setAuthoringReviewReportId(authoringReviewReportId);
	}, [authoringReviewReportId, setAuthoringReviewReportId]);

	const isOnCompetencyPage = matches.some((match) =>
		((match.handle as { tags?: string[] } | undefined)?.tags ?? []).includes(RouterTags.COMPETENCIES_ADMIN)
	);
	useEffect(() => {
		setIsOnCompetencyPage(isOnCompetencyPage);
	}, [isOnCompetencyPage, setIsOnCompetencyPage]);

	const conversationStarted = useAskPerformYardStore((state) =>
		state.messages.some((message) => message.role === "user")
	);
	const activeInterviewerHarness = useAskPerformYardStore((state) => state.activeInterviewerHarness);
	const setActiveInterviewerHarness = useAskPerformYardStore((state) => state.setActiveInterviewerHarness);
	const deactivateInterviewerHarness = useAskPerformYardStore((state) => state.deactivateInterviewerHarness);
	const pageActivatedHarnessRef = useRef(false);
	useEffect(() => {
		if (!competencyAgentVisible) return;
		if (isOnCompetencyPage) {
			// Pre-set on entry so the intro + "Upload a PDF" route deterministically (skip once a conversation exists).
			if (!conversationStarted) {
				setActiveInterviewerHarness("competency");
				pageActivatedHarnessRef.current = true;
			}
		} else if (pageActivatedHarnessRef.current && activeInterviewerHarness === "competency") {
			// Leaving the page undoes our own pre-activation; classifier-entered interviews are left alone.
			pageActivatedHarnessRef.current = false;
			deactivateInterviewerHarness();
		}
	}, [isOnCompetencyPage, competencyAgentVisible, conversationStarted, activeInterviewerHarness]);

	useEffect(() => {
		if (aiSettings?.conversationalAiSettings) {
			const { askHrEnabled, goalCreationEnabled, formCreationEnabled } = aiSettings.conversationalAiSettings;
			updateGreetingMessage({
				askHrEnabled,
				goalCreationEnabled,
				formCreationEnabled: (formCreationEnabled ?? false) && canCreateForms,
				competencyInterviewerEnabled: competencyAgentVisible,
				reportBuilderEnabled: reportBuilderVisible,
				isOnFormBuilderPage,
				isOnGoalCreationPage,
				isOnCompetencyPage,
				goalCoachingEnabled: goalCoachEnabled,
				formBuildingCoachEnabled: formBuildingCoachEnabled && canCreateForms,
			});
		}
	}, [
		aiSettings,
		canCreateForms,
		competencyAgentVisible,
		reportBuilderVisible,
		isOnFormBuilderPage,
		isOnGoalCreationPage,
		goalCoachEnabled,
		formBuildingCoachEnabled,
		isOnCompetencyPage,
		updateGreetingMessage,
	]);

	// Check if the company has goal categories configured
	const { data: goalCategoriesData } = useHasGoalCategories();

	useEffect(() => {
		if (goalCategoriesData !== undefined) {
			setHasGoalCategories(goalCategoriesData.hasCategories);
		}
	}, [goalCategoriesData, setHasGoalCategories]);

	// Check if the user has any AI-accessible documents
	const { data: accessibleDocs } = useHasAccessibleDocuments();

	useEffect(() => {
		if (accessibleDocs !== undefined) {
			setHasNoDocuments(!accessibleDocs.hasDocuments);
		}
	}, [accessibleDocs, setHasNoDocuments]);

	// Get current user ID to detect super user switches
	const userId = useSelector(getUserID);
	const prevUserIdRef = useRef(userId);

	// Reset chat when user ID changes (e.g., during super usering)
	useEffect(() => {
		if (prevUserIdRef.current !== userId && prevUserIdRef.current !== undefined) {
			resetChat();
		}
		prevUserIdRef.current = userId;
	}, [userId, resetChat]);

	if (!showLegacySurfaces && !triggerVisible) {
		return null;
	}

	return (
		<ChatErrorBoundary>
			{/* Mutually exclusive: useAskPerformYardVisible stands the FAB down wherever the trigger
			    renders, and the panel's empty state carries the coaches the FAB used to anchor. */}
			{showLegacySurfaces && (
				<>
					<GoalCoachPopup />
					<FormBuildingCoachPopup />
					<CompetencyCoachPopup />
					<ChatTrigger />
					<ChatContainer />
				</>
			)}
			{triggerVisible && (
				<ChatErrorBoundary fallbackPosition="panel">
					<AskPyPanel />
				</ChatErrorBoundary>
			)}
			<ViewDocumentModal
				show={documentModal.isOpen}
				onClose={closeDocumentModal}
				document={documentModal.document}
			/>
		</ChatErrorBoundary>
	);
};

export default AskPerformYard;
